import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { ContactInfo } from "../../Payables/ContactInfo";
import {
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Box,
  useTheme,
  Button,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grid2 from "@mui/material/Grid2";
import { useSupplier } from "../../../hooks/useSupplier";
import { useInvoiceExtractedData } from "../../../hooks/useInvoiceExtractedData";
import { useNominalCodes } from "../../../hooks/useNominalCodes";
import { useCurrencies } from "../../../hooks/useCurrencies";
import { useTaxes } from "../../../hooks/useTaxes";
import LoadingAiCard from "../../../components/LoadingAiCard/LoadingAiCard";
import {
  InvoiceExtractedResponseModel,
  InvoiceExtractedLineItemModel,
  InvoiceExtractedRequestModel,
} from "@attelas/shared";
import { Footer } from "../../../components/Footer";
import { BackButton } from "../../../components/BackButton";
import CheckIcon from "@mui/icons-material/Check";
import { useSendInvoiceExtractedData } from "../../../hooks/useSendInvoiceExtractedData";
import { Tip } from "../../../components/Tip";
import dayjs from "dayjs";
import { ToastContext } from "../../../context/ToastContext";

interface InvoiceFormData {
  invoiceDate: string;
  dueDate: string;
  reference: string;
  currency: string;
  isTaxIncluded: boolean | string;
  total: number;
  businessTenantId?: string | null;
  lineItems: InvoiceExtractedLineItemModel[];
}

const QuickAddInvoice = () => {
  const { supplierId, jobId } = useParams();
  const { supplier } = useSupplier(supplierId);
  const { invoiceExtractedData, isExtractedDataLoading } = useInvoiceExtractedData(jobId!);

  return (
    <Stack spacing={2} pb={8}>
      <ContactInfo
        title={supplier ? "Supplier" : ""}
        contactLink={supplier?.link}
        companyName={supplier?.companyName}
        accountNumber={supplier?.accountNumber}
      />
      {isExtractedDataLoading ? (
        <InvoiceExtractionLoading />
      ) : invoiceExtractedData ? (
        <InvoiceExtractionLoaded invoiceExtractedData={invoiceExtractedData} />
      ) : invoiceExtractedData === null ? (
        <Tip title="Invoice not found" />
      ) : (
        <InvoiceExtractionLoading />
      )}
    </Stack>
  );
};

const InvoiceExtractionLoading = () => {
  return (
    <Stack spacing={2} pb={8}>
      <LoadingAiCard message="Extracting invoice data, this may take a few minutes..." />
    </Stack>
  );
};

const InvoiceExtractionLoaded: React.FC<{ invoiceExtractedData: InvoiceExtractedResponseModel }> = ({
  invoiceExtractedData,
}) => {
  const { showToast } = useContext(ToastContext);
  const theme = useTheme();
  const { invoiceFileName } = useParams();
  const decodedInvoiceFileName = decodeURIComponent(invoiceFileName ?? "");
  const { nominalCodes } = useNominalCodes(invoiceExtractedData.businessTenantId);
  const { currencies } = useCurrencies(invoiceExtractedData.businessTenantId);
  const { taxes } = useTaxes(invoiceExtractedData.businessTenantId);
  const { sendInvoiceExtractedData, isLoading, isError } = useSendInvoiceExtractedData(invoiceExtractedData.id);

  const [formData, setFormData] = useState<InvoiceFormData>({
    invoiceDate: "",
    dueDate: "",
    reference: "",
    currency: "",
    lineItems: [],
    isTaxIncluded: false,
    total: 0,
  });

  const isTaxIncluded = formData.isTaxIncluded === "true" || formData.isTaxIncluded === true;

  useEffect(() => {
    const invoiceData = invoiceExtractedData.data.invoiceData;
    if (!invoiceData) return;
    const invoiceDate = invoiceData.invoiceDate ? dayjs(invoiceData.invoiceDate).format("YYYY-MM-DD") : "";
    const dueDate = invoiceData.dueDate ? dayjs(invoiceData.dueDate).format("YYYY-MM-DD") : "";
    setFormData({
      invoiceDate: invoiceDate,
      dueDate: dueDate,
      reference: invoiceData.reference,
      currency: invoiceData.currency,
      lineItems: invoiceData.lineItems,
      isTaxIncluded: invoiceData.isTaxIncluded,
      total: invoiceData.total,
    });
  }, [invoiceExtractedData]);

  useEffect(() => {
    if (isError) {
      showToast("Failed to submit invoice, please try again later", "error");
    }
  }, [isError]);

  const handleInvoiceFieldChange = (field: keyof InvoiceFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLineItemChange = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleNominalCodeChange = (index: number, nominalCodeStr: string) => {
    const nominalCode = nominalCodes?.find((nc) => nc.code === nominalCodeStr);
    if (nominalCode) {
      handleLineItemChange(index, "nominalCode", nominalCode);
    }
  };

  const handleTaxChange = (index: number, taxId: string) => {
    const tax = taxes?.find((t) => t.id === taxId);
    if (tax) {
      handleLineItemChange(index, "tax", tax);
      handleLineItemChange(index, "taxRate", tax.rate);
    }
  };

  const calculateSubtotal = (lineItems: InvoiceExtractedLineItemModel[]) => {
    return lineItems.reduce((acc, item) => acc + item.lineSubtotal, 0);
  };

  const calculateTaxTotal = (lineItems: InvoiceExtractedLineItemModel[]) => {
    return lineItems.reduce((acc, item) => acc + item.lineTax, 0);
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleSubmit = async () => {
    const invoiceData = invoiceExtractedData.data.invoiceData;
    if (!invoiceData) return;
    const request: InvoiceExtractedRequestModel = {
      invoiceData: {
        ...invoiceData,
        ...formData,
        isTaxIncluded: isTaxIncluded,
      },
    };
    const result = await sendInvoiceExtractedData(request);
    if (result.result === "Success") {
      showToast("Invoice submitted successfully", "success");
    } else {
      showToast("Failed to submit invoice, please try again later", "error");
    }
    handleBack();
  };

  return (
    <Stack spacing={2}>
      <Accordion defaultExpanded sx={{ backgroundColor: "transparent" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 1 }}>
          <Typography variant="h2">Invoice {decodedInvoiceFileName}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1 }}>
          <Stack spacing={1.5}>
            <Grid2 container spacing={1}>
              <Grid2 size={6}>
                <TextField
                  fullWidth
                  label="Invoice Date"
                  type="date"
                  size="small"
                  value={formData.invoiceDate}
                  onChange={(e) => handleInvoiceFieldChange("invoiceDate", e.target.value)}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  size="small"
                  value={formData.dueDate}
                  onChange={(e) => handleInvoiceFieldChange("dueDate", e.target.value)}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid2>
            </Grid2>
            <TextField
              fullWidth
              label="Reference"
              size="small"
              value={formData.reference}
              onChange={(e) => handleInvoiceFieldChange("reference", e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                onChange={(e: SelectChangeEvent) => handleInvoiceFieldChange("currency", e.target.value)}
              >
                {currencies?.map((currency) => (
                  <MenuItem key={currency.id} value={currency.code}>
                    {currency.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Tax Included</InputLabel>
                <Select
                  value={isTaxIncluded ? "true" : "false"}
                  label="Tax Included"
                  onChange={(e: SelectChangeEvent) => handleInvoiceFieldChange("isTaxIncluded", e.target.value)}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
              {isTaxIncluded && (
                <TextField
                  fullWidth
                  label="Tax Rate"
                  size="small"
                  value={calculateTaxTotal(formData.lineItems)}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    input: {
                      endAdornment: <InputAdornment position="end">{formData.currency}</InputAdornment>,
                    },
                  }}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              {isTaxIncluded && (
                <TextField
                  fullWidth
                  label="Subtotal"
                  size="small"
                  value={calculateSubtotal(formData.lineItems)}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    input: {
                      endAdornment: <InputAdornment position="end">{formData.currency}</InputAdornment>,
                    },
                  }}
                />
              )}
              <TextField
                fullWidth
                label={`Total (${isTaxIncluded ? "Incl" : "Excl"} Tax)`}
                size="small"
                value={formData.total}
                onChange={(e) => handleInvoiceFieldChange("total", e.target.value)}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  input: {
                    endAdornment: <InputAdornment position="end">{formData.currency}</InputAdornment>,
                  },
                }}
              />
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Items Section */}
      <Accordion defaultExpanded sx={{ backgroundColor: "transparent" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 1 }}>
          <Typography variant="h2">Items</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1 }}>
          <Stack spacing={2}>
            {formData.lineItems.map((item, index) => (
              <Box key={index} sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}` }}>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Description"
                    size="small"
                    variant="outlined"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                    multiline
                    rows={2}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />

                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Tax Rate</InputLabel>
                    <Select
                      value={item.tax?.id || ""}
                      label="Tax Rate"
                      onChange={(e: SelectChangeEvent) => handleTaxChange(index, e.target.value)}
                    >
                      {taxes?.map((tax) => (
                        <MenuItem key={tax.id} value={tax.id}>
                          {tax.name} ({tax.rate}%)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={item.nominalCode?.code || ""}
                      label="Category"
                      onChange={(e: SelectChangeEvent) => handleNominalCodeChange(index, e.target.value)}
                    >
                      {nominalCodes?.map((nominalCode) => (
                        <MenuItem key={nominalCode.code} value={nominalCode.code}>
                          {nominalCode.code} - {nominalCode.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      size="small"
                      value={item.quantity}
                      onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                        input: {
                          type: "number",
                        },
                        htmlInput: {
                          type: "number",
                          min: 1,
                          step: 1,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Unit Price"
                      size="small"
                      value={item.unitPrice}
                      onChange={(e) => handleLineItemChange(index, "unitPrice", e.target.value)}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                        input: {
                          endAdornment: <InputAdornment position="end">{formData.currency}</InputAdornment>,
                        },
                      }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      label="Tax Rate"
                      size="small"
                      value={item.taxRate}
                      onChange={(e) => handleLineItemChange(index, "taxRate", e.target.value)}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                        input: {
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          type: "number",
                        },
                        htmlInput: {
                          type: "number",
                          min: 0,
                          max: 100,
                          step: 0.1,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Total"
                      size="small"
                      value={item.lineSubtotal}
                      onChange={(e) => handleLineItemChange(index, "lineSubtotal", e.target.value)}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                        input: {
                          endAdornment: <InputAdornment position="end">{formData.currency}</InputAdornment>,
                        },
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Footer>
        <Grid2 container spacing={1} justifyContent="space-between" width="100%">
          <Grid2 size={6}>
            <BackButton onClick={handleBack} />
          </Grid2>
          <Grid2 size={6} textAlign="right">
            <Button endIcon={<CheckIcon />} onClick={handleSubmit} variant="contained" disabled={isLoading}>
              Submit
            </Button>
          </Grid2>
        </Grid2>
      </Footer>
    </Stack>
  );
};

export default QuickAddInvoice;
